/**
 * ClientLinkExpired: the only thing a client sees when their link cannot
 * be served.
 *
 * BM-H604 (23 Sep 2026). A client following an emailed link must never be
 * shown the staff Microsoft login. Wherever the app would otherwise send a
 * visitor to /login, a client-facing page (config/clientFacingRoutes.js)
 * renders this instead. Plain text, sans serif, the same light surface as
 * the existing client pages (ClientPortal's expired screen), and nothing
 * else: no sign-in button, no staff navigation, no branding chrome.
 */
import { useEffect, useState } from "react";
import {
  CLIENT_LINK_EXPIRED_MESSAGE,
  CLIENT_LINK_EXPIRED_EVENT,
  isClientFacingPath,
} from "../config/clientFacingRoutes";

const pageStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 10000,
  background: "#FAFAF8",
  color: "#1e293b",
  overflowY: "auto",
  fontFamily: "system-ui, -apple-system, \"Segoe UI\", Roboto, Helvetica, Arial, sans-serif",
};

export default function ClientLinkExpired() {
  return (
    <div
      style={pageStyle}
      className="light-theme flex items-center justify-center p-4"
      data-testid="client-link-expired"
      role="alert"
    >
      <div className="max-w-md text-center">
        <p style={{ fontSize: "1rem", lineHeight: 1.6 }}>{CLIENT_LINK_EXPIRED_MESSAGE}</p>
      </div>
    </div>
  );
}

/**
 * Mounted once in App. Listens for the interceptor's client-link-expired
 * event and, while the browser is on a client-facing page, covers the page
 * with the plain client message. On staff pages it renders nothing.
 */
export function ClientLinkExpiredOverlay({ pathname }) {
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    const onExpired = () => setExpired(true);
    window.addEventListener(CLIENT_LINK_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(CLIENT_LINK_EXPIRED_EVENT, onExpired);
  }, []);
  if (!expired || !isClientFacingPath(pathname)) return null;
  return <ClientLinkExpired />;
}
